import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Form } from 'antd'
import NumberInput, { FormatInputValueFunction, NumberFormatProps } from 'react-number-format'
import { i18n } from 'LibNeo/utils/intl'
import { TooltipLabelComponent } from 'LibNeo/components'
import { XOR, trimPasteText, getOnChangeValue, onBlurTrim } from 'Helpers'
import { FieldNames } from 'Constants/FieldNames'
import { GetFieldDecoratorOptions, WrappedFormUtils } from 'antd/lib/form/Form'
import { Callback } from 'Src/types/utilTypes'
import { ErrorText, PrependLine, StyledSelectAntdOption } from '../Select/styled'
import { AutoCompleteStyled, InputStyled, SelectWithOtherWrapper, Icon } from './styled'

type ComponentProps = {
	items: string[]
	onSelectOther?: (value: string) => void // вызывается при выборе Иное
	onSelectItem?: (value: string) => void // вызывается при выборе значения из селекта
	onSearch?: (value: string) => void
	formatItems?: FormatInputValueFunction // предварительное форматирование значений
	showArrow?: boolean
	allowClear?: boolean
	disabled?: boolean
	dataQa?: string
	placeholder?: string
	label?: string
	link?: string
	href?: string
	linkAction?: Callback
	tooltip?: string
	className?: string
	requiredHighlight?: boolean
	isTrimData?: boolean
	skipFilterItems?: boolean
	onFocus?: Callback
	onChange?: (value: string) => void
} & Omit<NumberFormatProps, 'form'> &
	XOR<
		{
			fieldName: FieldNames | string
			form: WrappedFormUtils
			fieldOptions?: GetFieldDecoratorOptions
		},
		{
			value: string
			errorText?: string
		}
	>
export const SelectWithOther: FC<ComponentProps> = ({
	items,
	fieldName,
	dataQa = 'select_with_other',
	disabled = false,
	placeholder,
	label,
	tooltip,
	className,
	form,
	onChange,
	onSelectOther,
	onSelectItem,
	onSearch,
	allowClear = true,
	showArrow = true,
	errorText,
	fieldOptions,
	link,
	href,
	linkAction,
	formatItems,
	value,
	requiredHighlight,
	isTrimData,
	format,
	mask,
	skipFilterItems,
	onFocus,
}) => {
	const getFormatValue = (data: ComponentProps['items']) =>
		formatItems ? [...new Set(data?.map(formatItems).filter(Boolean))] : data?.filter(Boolean)
	const [dataSource, setDataSource] = useState<ComponentProps['items']>(getFormatValue(items))
	const selectWrapper = useRef<HTMLDivElement>(null)

	const valueFromInput = form && fieldName ? form.getFieldValue(fieldName) : value

	const filterDataSource = (searchText: string) =>
		setDataSource(
			!searchText || skipFilterItems
				? getFormatValue(items)
				: getFormatValue(
						items.filter(
							(v) =>
								v?.toLowerCase().includes(searchText.toLowerCase()) ||
								formatItems?.(v).toLowerCase().includes(searchText.toLowerCase())
						)
				  )
		)

	useEffect(() => {
		setDataSource(getFormatValue(items))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [items.length])

	const onChangeValue = (v: string) => {
		onSearch?.(v)
		onChange?.(v)
	}
	const onDropdownVisibleChange = (visible: boolean) => {
		if (visible) return
		if (items.includes(valueFromInput)) {
			onSelectItem?.(valueFromInput)
		} else {
			onSelectOther?.(valueFromInput)
		}
		setDataSource(getFormatValue(items))
	}

	const dataSourceElements = useMemo(() => {
		if (!dataSource.length && valueFromInput)
			return [
				<StyledSelectAntdOption key={valueFromInput}>{valueFromInput}</StyledSelectAntdOption>,
			]
		return dataSource.map((item) => (
			<StyledSelectAntdOption key={item}>{item}</StyledSelectAntdOption>
		))
	}, [dataSource, valueFromInput])

	const hasError = Boolean(errorText || form?.getFieldError(fieldName))
	const isRequired = Boolean(fieldOptions?.rules?.find((v) => Boolean(v.required)))
	const pHolder = i18n(placeholder ?? 'common.select_with_other_default_placeholder')

	const valueProps = form ? {} : { value }

	const propsForTrim = isTrimData
		? {
				onBlur: onBlurTrim({
					form,
					fieldName,
					onChange,
					changeValue: () => undefined,
					currentValue: value,
				}),
				onPaste: trimPasteText(
					getOnChangeValue({ form, fieldName, onChange, changeValue: () => undefined })
				),
		  }
		: {}

	const icon = showArrow && !valueFromInput ? <Icon name="down" /> : undefined

	const defaultInput = (
		<InputStyled
			$hasError={hasError}
			placeholder={pHolder}
			allowClear={allowClear}
			suffix={icon}
			disabled={disabled}
			{...propsForTrim}
		/>
	)

	const AntWithIcon = useCallback(
		(props) => <InputStyled $hasError={hasError} suffix={icon} disabled={disabled} {...props} />,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	)

	const maskedInput = (
		<NumberInput format={format} mask={mask} placeholder={placeholder} customInput={AntWithIcon} />
	)

	const autocomplete = (
		<AutoCompleteStyled
			data-qa={dataQa}
			disabled={disabled}
			data-qa-disabled={disabled}
			className={className}
			notFoundContent={null}
			showSearch
			onDropdownVisibleChange={onDropdownVisibleChange}
			onSearch={filterDataSource}
			onSelect={onSelectItem}
			getPopupContainer={() => selectWrapper.current}
			onChange={onChangeValue}
			showArrow={false}
			allowClear={allowClear}
			dataSource={dataSourceElements}
			$hasError={hasError}
			$isEmpty={!icon}
			highligth={requiredHighlight}
			{...valueProps}
			onFocus={onFocus}
		>
			{format ? maskedInput : defaultInput}
		</AutoCompleteStyled>
	)
	return (
		<SelectWithOtherWrapper ref={selectWrapper} isEmpty={!dataSource.length}>
			{(label || tooltip) && (
				<PrependLine>
					<TooltipLabelComponent
						title={i18n(label)}
						required={isRequired}
						tooltip={tooltip}
						link={link}
						href={href}
						linkAction={linkAction}
					/>
				</PrependLine>
			)}
			{form ? (
				<Form.Item>
					{form.getFieldDecorator(fieldName as string, {
						...(fieldOptions || {}),
						normalize: formatItems || fieldOptions?.normalize,
					})(autocomplete)}
				</Form.Item>
			) : (
				autocomplete
			)}
			<ErrorText hasError={hasError}>
				<span>{errorText}</span>
			</ErrorText>
		</SelectWithOtherWrapper>
	)
}
